import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import type { TopicAction } from '@stores/topic/action';
import PC from './layout/pc';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import type { BasePageState } from '../../types/base';
import Toast from '@components/Toast';
import { applyDefaultSEO, applySEO, buildTopicDetailSEO } from '@utils/seo';
import { readTopicThreadList } from '@server';

interface RouteParams {
    id: string;
}
interface TopicDetailProps extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
    topic?: TopicAction;
}

interface TopicDetailState extends BasePageState {
    topisDetail: any
    navList: any
    navCurrentIndex: any
    navCurrentType: any
    filterList: any
    filterCurrentIndex: any
    filterCurrentType: any
    idsArray: any
    isArticle: any
    hasVideo: any
    showType: any
    stickyThreadList: any[]
    stickyListLoading: boolean
    topicThreadLoading: boolean
}
@inject('app', 'user', 'topic')
@observer
class TopicDetail extends BasePage<TopicDetailProps, TopicDetailState> {
    topicId: any = '';   // 话题id
    // 用于识别当前 tab 的列表请求，防止旧请求结束后关闭最新请求的加载态。
    topicThreadLoadVersion = 0;
    getBaseNavList = () => ([
        { name: '综合', id: 0, type: '' },
        { name: '图文', id: 0, type: '1' },
        { name: '视频', id: 0, type: '2' },
    ]);
    // 固定分类中仅“综合”置前，关联话题（动态 tab）紧随其后，图文/视频置后。
    buildNavList = (associatedTopics: any[] = []) => {
        const baseNavList = this.getBaseNavList();
        return [baseNavList[0], ...associatedTopics, ...baseNavList.slice(1)];
    }
    state = {
        loading: false,
        error: null,
        pageLoaded: false,
        topisDetail: {},
        navList: this.getBaseNavList(),
        navCurrentIndex: 0, // 导航当前选中索引
        navCurrentType: 0,  // 导航当前选中类型
        isArticle: '',  // 是图文
        hasVideo: '',   // 是视频
        idsArray: [],   // 筛选相关话题id下的帖子列表
        filterList: [
            { name: '默认', id: 0, type: 'posted' },
            { name: '热门', id: 1, type: '' },
            { name: '最新', id: 2, type: 'created' },
        ],
        filterCurrentIndex: 0,  // 筛选当前选中索引
        filterCurrentType: 'posted',  // 筛选当前选中类型
        showType: 1,
        stickyThreadList: [],
        stickyListLoading: false,
        topicThreadLoading: false,
    }

    buildTopicThreadParams = (pageNumber = 1, pageSize = 20) => {
        const { idsArray, isArticle, hasVideo, filterCurrentType } = this.state;
        const params: any = {
            'page[number]': pageNumber,
            'page[size]': pageSize,
            sort: filterCurrentType,
        };

        if (isArticle) params['filter[isArticle]'] = isArticle;
        if (hasVideo) params['filter[hasVideo]'] = hasVideo;
        if (idsArray && idsArray.length > 0) {
            params['filter[ids]'] = idsArray.map(id => String(id)).join(',');
        }

        return params;
    }

    fetchStickyThreadList = async (loadVersion = this.topicThreadLoadVersion) => {
        const currentNav = this.state.navList[this.state.navCurrentIndex];
        // 关联话题插入后不能再依赖固定下标，图文和视频不显示置顶帖。
        if (currentNav?.type === '1' || currentNav?.type === '2') {
            if (loadVersion === this.topicThreadLoadVersion) {
                this.setState({ stickyThreadList: [], stickyListLoading: false });
            }
            return;
        }

        const params = {
            ...this.buildTopicThreadParams(1, 20),
            'filter[isSticky]': true,
        };

        this.setState({ stickyListLoading: true });
        try {
            const res = await readTopicThreadList(params);
            const success = res?.code === 0 || res?.Code === 0;
            const resData = res?.data || res?.Data;
            if (loadVersion === this.topicThreadLoadVersion) {
                this.setState({
                    stickyThreadList: success ? (resData?.data || []) : [],
                    stickyListLoading: false,
                });
            }
        } catch (error) {
            console.error('[topicDetail] 获取置顶帖子列表失败:', error);
            if (loadVersion === this.topicThreadLoadVersion) {
                this.setState({ stickyThreadList: [], stickyListLoading: false });
            }
        }
    }
    async componentDidMount() {
        applyDefaultSEO();
        const { app, user, topic, match, location } = this.props;
        this.topicId = match.params.id;

        if (match.params.id) {
            console.log('参数id, ', match.params.id);
            // 设置 header 中的话题选中状态
            if (topic) {
                topic.setFollowTopicFocusId(match.params.id);
            }

            this.setState({
                idsArray: [match.params.id]
            }, () => {
                this.loadTopicData();
            })
        }

        super.componentDidMount();
        const routerParams = new URLSearchParams(this.props.location.search);
        // 获取路由参数，用来判断需不需要显示话题详情部分
        // 只有当 type 明确等于 '0' 时才不显示，其他情况（包括不传 type）都显示
        const typeParam = routerParams?.get('type');
        this.setState({
            showType: typeParam === '0' ? 0 : 1
        })
    }

    componentDidUpdate(prevProps: any) {
        const { match, location, topic } = this.props;
        let navListNew = this.getBaseNavList();

        // 监听路由参数变化，如果话题 ID 改变，重新加载数据
        if (prevProps.match.params.id !== match.params.id) {
            applyDefaultSEO();
            this.topicId = match.params.id;

            // 滚动到顶部
            window.scrollTo(0, 0);

            // 更新 header 中的话题选中状态
            if (topic) {
                topic.setFollowTopicFocusId(match.params.id);
            }

            // 清空列表数据，重置分页
            if (topic && topic.listStore) {
                topic.listStore.clearList({ namespace: 'topicThreadList' });
            }

            // 同步 idsArray 到当前话题，并重置导航/筛选状态
            const currentId = String(this.topicId);
            const routerParams = new URLSearchParams(this.props.location.search);
            // 获取路由参数，用来判断需不需要显示话题详情部分
            // 只有当 type 明确等于 '0' 时才不显示，其他情况（包括不传 type）都显示
            const typeParam = routerParams?.get('type');
            this.setState({
                idsArray: [currentId],
                showType: typeParam === '0' ? 0 : 1,
                navCurrentIndex: 0,
                navCurrentType: 0,
                isArticle: '',
                hasVideo: '',
                filterCurrentIndex: 0,
                filterCurrentType: 'posted',
                navList: navListNew,
                stickyThreadList: [],
                topicThreadLoading: true,
            }, () => {
                this.loadTopicData(++this.topicThreadLoadVersion);
            });
        }
    }

    // 加载话题数据
    loadTopicData = async (loadVersion = ++this.topicThreadLoadVersion) => {
        const { app, user, topic } = this.props;
        if (!topic) return;

        this.setState({ topicThreadLoading: true });

        // 获取话题详情
        topic.getTopicDetail(this.topicId).then(res => {
            if(res.code == 0) {
                applySEO(buildTopicDetailSEO(res.data));
                const associatedTopics = Array.isArray(res.data?.associatedTopics)
                    ? res.data.associatedTopics
                    : [];
                this.setState({
                    topisDetail: res.data,
                    navList: this.buildNavList(associatedTopics)
                });
            } else {
                Toast.info(`${res.msg}`);
                setTimeout(() => {
                    this.props.history.push(`/`);
                }, 300)
            }
            
        }).catch(error => {
            console.error('[topicDetail] 获取话题详情失败:', error);
        });

        // 获取话题帖子列表
        const params = this.buildTopicThreadParams(1, 20);
        await topic.getTopicThreadList(params).then((res: any) => {
        }).catch(error => {
            console.error('[topicDetail] 获取话题帖子列表失败:', error);
        }).finally(() => {
            if (loadVersion === this.topicThreadLoadVersion) {
                this.setState({ topicThreadLoading: false });
            }
        });
        if (loadVersion === this.topicThreadLoadVersion) {
            this.fetchStickyThreadList(loadVersion);
        }
    }
    // 获取话题帖子列表
    getTopicThreadListFunction = async () => {
        const { app, user, topic } = this.props;
        this.setState({ topicThreadLoading: true });
        const params = this.buildTopicThreadParams(1, 20);
        await topic.getTopicThreadList(params).then((res: any) => {
        }).catch(error => {
            console.error('[topicDetail] 获取话题帖子列表失败:', error);
        }).finally(() => {
            this.setState({ topicThreadLoading: false });
        });
        this.fetchStickyThreadList();
    }

    // 加载下一页数据
    handleLoadMore = () => {
        console.log('分页加载');
        const { topic } = this.props;
        let { idsArray } = this.state;
        if (!topic) return;

        const list = topic.listStore.lists['topicThreadList'];
        if (!list) return;

        // 如果列表为空，说明是刚切换/清空，第一页数据由 loadTopicData 加载，不在这里加载
        const flatList = topic.listStore.getList({ namespace: 'topicThreadList' });
        if (!flatList || flatList.length === 0) {
            console.log('列表为空，跳过加载更多');
            return;
        }

        const currentPage = list.attribs?.currentPage || list.attribs?.page || 1;
        const nextPage = currentPage + 1;
        const pageSize = list.attribs?.pageSize || 20;

        // 获取话题帖子列表
        const params: any = this.buildTopicThreadParams(nextPage, pageSize);

        // 将多个 filter[ids][] 合并为一个 filter[ids] 逗号分隔字符串
        if (idsArray && idsArray.length > 0) {
            // 确保所有 ID 都是字符串格式，然后转为逗号分隔
            params['filter[ids]'] = idsArray.map(id => String(id)).join(',');
        }

        topic.getTopicThreadList(params).then((res: any) => {
        })
    }

    // 导航点击，类型切换
    navClick = (item: any, index: any) => {
        const { topic } = this.props;
        // 滚动到顶部
        window.scrollTo(0, 0);
        // 清空列表数据，重置分页
        if (topic && topic.listStore) {
            topic.listStore.clearList({ namespace: 'topicThreadList' });
        }

        let newIsArticle = '';
        let newHasVideo = '';
        let newIdsArray: string[] = [String(this.topicId)];  // 永远包含当前话题ID

        if (item.id == 0) {
            // 点击综合、图文、视频等基础分类
            if (item.type == 1) {  // 图文
                newIsArticle = '1';
            } else if (item.type == 2) {  // 视频
                newHasVideo = '1';
            }
            // 点击基础分类时，数组重置为只包含当前话题ID
            newIdsArray = [String(this.topicId)];
        } else {
            // ✅ 修复：点击关联话题时的处理
            const clickedId = String(item.id);

            // 如果点击的是当前话题自身，不重复添加
            if (clickedId === String(this.topicId)) {
                console.log('⏭️ 点击的是当前话题自身，不重复添加');
            } else {
                // ✅ 关键修复：检查是否已存在，避免重复添加
                const existingIndex = newIdsArray.findIndex(id => id === clickedId);

                if (existingIndex === -1) {
                    // 不存在，才添加
                    newIdsArray.push(clickedId);
                    console.log(`✅ 添加关联话题 ID ${clickedId}`);
                } else {
                    // ✅ 已存在，移除（实现切换效果）
                    newIdsArray = newIdsArray.filter(id => id !== clickedId);
                    console.log(`➖ 移除关联话题 ID ${clickedId}`);
                }
            }
        }

        // 确保数组最多2个元素
        if (newIdsArray.length > 2) {
            newIdsArray = newIdsArray.slice(0, 2);
        }

        console.log('📦 最终 idsArray:', newIdsArray);

        this.setState({
            navCurrentIndex: index,
            navCurrentType: item.type,
            isArticle: newIsArticle,
            hasVideo: newHasVideo,
            idsArray: newIdsArray,
            filterCurrentIndex: 0,
            filterCurrentType: 'posted',
            topicThreadLoading: true,
            stickyThreadList: [],
            stickyListLoading: false,
        }, () => {
            this.loadTopicData(++this.topicThreadLoadVersion);
        });
    };

    // 筛选切换
    filterClick = (item: any, index: any) => {
        console.log(item, '当前');
        const { topic } = this.props;
        // 滚动到顶部
        window.scrollTo(0, 0);
        // 清空列表数据，重置分页
        if (topic && topic.listStore) {
            topic.listStore.clearList({ namespace: 'topicThreadList' });
        }
        this.setState({
            filterCurrentIndex: index,
            filterCurrentType: item.type,
            topicThreadLoading: true,
            stickyThreadList: [],
            stickyListLoading: false,
        }, () => {
            this.loadTopicData(++this.topicThreadLoadVersion);
        });
    }

    // 关注/取消关注
    handleFollowToggle = async () => {
        const { topic } = this.props;
        const { id, isFollowed } = topic.topicDetail || {};
        if (!id) {
            console.warn('话题ID不存在');
            return;
        }

        if (isFollowed) {
            // 取消关注
            await topic.createTopicsUnFollow(id);
        } else {
            // 关注
            await topic.createTopicsFollow(id);
        }
    };
    render() {
        const { app } = this.props;
        const platform = app?.platform || 'h5';
        let { navList, filterList, navCurrentIndex, filterCurrentIndex, idsArray } = this.state;
        // 如果平台信息还未初始化，显示加载状态
        if (!app?.platform) {
            return (
                <div className={styles.loading} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}>
                    <div>加载中...</div>
                </div>
            );
        } else {
            return (<div className={platform === 'pc' ? styles.pcWrap : styles.h5Wrap}>
                {platform === 'pc' ? (<PC
                    handleSetState={(data, rep) => this.setState({ ...data })}
                    {...this.props}
                    navList={navList}
                    navCurrentIndex={navCurrentIndex}
                    filterList={filterList}
                    handleLoadMore={this.handleLoadMore}
                    navClick={this.navClick}
                    filterClick={this.filterClick}
                    filterCurrentIndex={filterCurrentIndex}
                    handleFollowToggle={this.handleFollowToggle}
                    idsArray={idsArray}
                    showType={this.state.showType}
                    stickyThreadList={this.state.stickyThreadList}
                    stickyListLoading={this.state.stickyListLoading}
                    topicThreadLoading={this.state.topicThreadLoading}
                ></PC>
                ) : (
                    <H5
                        handleSetState={(data, rep) => this.setState({ ...data })}
                        {...this.props}
                        navList={navList}
                        navCurrentIndex={navCurrentIndex}
                        filterList={filterList}
                        handleLoadMore={this.handleLoadMore}
                        navClick={this.navClick}
                        filterClick={this.filterClick}
                        filterCurrentIndex={filterCurrentIndex}
                        handleFollowToggle={this.handleFollowToggle}
                        idsArray={idsArray}
                        showType={this.state.showType}
                        stickyThreadList={this.state.stickyThreadList}
                        stickyListLoading={this.state.stickyListLoading}
                        topicThreadLoading={this.state.topicThreadLoading}
                    ></H5>
                )}
            </div>)
        }

    }
}

export default withRouter(TopicDetail);
